/* InquiryModal — customer-facing "Request consultation" form.
 *
 * Reused from the listing card, the profile detail page, and a future
 * "request without a specific pro" CTA. POSTS to /api/inquiries which
 * accepts an optional `professional_id` so the same form can route the
 * request to a specific pro or to the central admin queue.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2, ShieldCheck, AlertTriangle, CheckCircle2, Send } from "lucide-react";
import { categoryHeadline } from "@/professionals/categories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatErr = (detail, fallback) => {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const InquiryModal = ({ open, onClose, professional }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    city: "",
    plot_size: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!open) {
      setForm({ name: "", phone: "", city: "", plot_size: "", description: "" });
      setError("");
      setSuccess(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim() || !form.city.trim() || form.description.trim().length < 10) {
      setError("Please fill name, phone, city, and a short requirement (10+ characters).");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        plot_size: form.plot_size.trim() || undefined,
        description: form.description.trim(),
        professional_id: professional?.id,
        category: professional?.category,
      };
      const { data } = await axios.post(`${API}/inquiries`, body);
      setSuccess(data);
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail, "Could not submit your inquiry. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm grid place-items-center px-4 py-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="inquiry-modal"
    >
      <div
        className="bg-white text-foreground rounded-3xl w-full max-w-lg max-h-full overflow-hidden shadow-2xl border-2 border-gold/30 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-navy text-white p-5 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-gold font-bold">
              Custom Design Request
            </div>
            <div className="text-[15px] sm:text-base font-extrabold tracking-tight truncate">
              {professional ? `Request consultation · ${professional.full_name}` : "Connect with a Verified Professional"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="inquiry-close"
            aria-label="Close"
            className="h-9 w-9 rounded-xl bg-white/10 hover:bg-white/20 grid place-items-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {success ? (
          <div className="p-6 text-center space-y-3" data-testid="inquiry-success">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center border-2 border-emerald-300">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h2 className="text-lg font-extrabold text-navy">Inquiry submitted</h2>
            <p className="text-[13.5px] text-muted-foreground leading-relaxed">
              We've received your requirement and shared it with the professional.
              You'll receive a call or WhatsApp message at <strong>+91 {form.phone}</strong> within
              one working day.
            </p>
            <div className="text-[11px] text-muted-foreground">
              Reference no: <span className="font-mono font-bold">{success.id}</span>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="inquiry-success-close"
              className="mt-2 min-h-[44px] px-5 rounded-xl bg-navy text-gold font-extrabold text-[13.5px]"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="p-5 sm:p-6 space-y-4 overflow-y-auto max-h-[78vh]" data-testid="inquiry-form">
            {professional && (
              <div className="rounded-xl bg-sand-alt border border-border px-3 py-2 text-[12.5px]">
                <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-bold">
                  Sending to
                </div>
                <div className="font-bold text-navy">{professional.full_name}</div>
                <div className="text-muted-foreground">
                  {categoryHeadline(professional.category)} · {professional.city}
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 rounded-xl bg-red-50 border-2 border-red-200 px-3 py-2 text-[13px] text-red-800 font-semibold" data-testid="inquiry-error" role="alert">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Full name" required>
                <input
                  value={form.name}
                  onChange={setField("name")}
                  required
                  maxLength={80}
                  data-testid="inquiry-name"
                  className="w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
                  placeholder="e.g. Ramesh Kumar"
                />
              </Field>
              <Field label="Mobile" required>
                <div className="flex items-center gap-2 h-11 rounded-xl border-2 border-border bg-white overflow-hidden focus-within:border-navy">
                  <span className="pl-3 pr-1 text-[14px] font-bold text-navy tabular-nums">+91</span>
                  <input
                    value={form.phone}
                    onChange={setField("phone")}
                    required
                    inputMode="numeric"
                    maxLength={14}
                    data-testid="inquiry-phone"
                    className="flex-1 h-full bg-transparent text-[14px] tabular-nums outline-none"
                    placeholder="98765 43210"
                  />
                </div>
              </Field>
              <Field label="City" required>
                <input
                  value={form.city}
                  onChange={setField("city")}
                  required
                  maxLength={60}
                  data-testid="inquiry-city"
                  className="w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
                  placeholder="e.g. Patna"
                />
              </Field>
              <Field label="Plot size (optional)">
                <input
                  value={form.plot_size}
                  onChange={setField("plot_size")}
                  maxLength={40}
                  data-testid="inquiry-plot"
                  className="w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
                  placeholder="e.g. 30x40"
                />
              </Field>
            </div>

            <Field label="Requirement" required>
              <textarea
                value={form.description}
                onChange={setField("description")}
                required
                rows={4}
                minLength={10}
                maxLength={3000}
                data-testid="inquiry-description"
                className="w-full px-3 py-2 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy resize-y"
                placeholder="Tell us what you need — plot size, rooms, budget, vastu preference, deadline, etc."
              />
            </Field>

            <button
              type="submit"
              disabled={submitting}
              data-testid="inquiry-submit"
              className="w-full min-h-[48px] rounded-2xl bg-gold text-navy font-extrabold text-[14px] flex items-center justify-center gap-2 hover:bg-gold-dark hover:text-white shadow-[0_8px_18px_-8px_rgba(212,162,42,0.6)] transition-all disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" /> Submit inquiry
                </>
              )}
            </button>
            <p className="text-[11px] text-muted-foreground text-center leading-snug inline-flex items-start gap-1.5">
              <ShieldCheck className="h-3 w-3 mt-0.5 shrink-0" />
              Your details are shared only with the assigned professional and the Mera Naksha team.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-[0.18em] text-navy/70 font-bold">
      {label} {required && <span className="text-red-600">*</span>}
    </span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export default InquiryModal;
